import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ReactComponent as ArrowDownIcon } from './arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from './arrow-up.svg';
import { CheckBox } from '../CheckBox/CheckBox';
import { classNames } from '../utils';
import { MENU_DIRECTION, TRANSACTIONS_TABLE_STATUS } from 'components/constants';
import './InputDropDown.scss';

export const InputDropDown = ({
  options,
  specificOptions,
  name,
  error,
  value,
  currency,
  onChange,
  onBlur = () => {},
  label = '',
  placeholder,
  subText,
  className,
  topLabel,
  menuDirection,
  isRequired,
  isMulti,
  isDisabled,
  isSpecificValue,
  isTransactionStatuses,
  isAbsoluteErrorPosition,
  usePortal = true
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const menuId = useId();
  const inputClasses = classNames({
    'input-wrapper-dropdown': true,
    active: isMenuOpen || value?.length,
    disabled: isDisabled,
    error: error,
    'multi-select': isMulti,
    [className]: true
  });

  useEffect(() => {
    if (!isMenuOpen || !usePortal) return;
    const updateMenuPosition = () => {
      if (!dropdownRef?.current) return;
      const rect = dropdownRef.current?.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.top + rect.height + 2,
        left: rect.left,
        width: rect.width,
        zIndex: 9999
      });
    };
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isMenuOpen, usePortal]);

  const getValue = () => {
    if (isSpecificValue) return value;

    if (isMulti) {
      const multiValue = options?.filter((option) => value?.includes(option?.key));
      return multiValue
        .map((val) => {
          if (val?.currency) {
            return val?.currency + ' ' + val?.value;
          } else {
            return val?.value;
          }
        })
        ?.join('/');
    }
    return options?.find((option) => option?.key === value)?.value;
  };

  const inputChange = (data) => {
    if (isMulti) {
      let valueSet;
      if (isTransactionStatuses && name === 'statuses' && data === TRANSACTIONS_TABLE_STATUS.processing) {
        valueSet = value?.includes(data)
          ? // eslint-disable-next-line max-len
            value?.filter(
              (val) =>
                val !== data &&
                val !== TRANSACTIONS_TABLE_STATUS.acceptRequest &&
                val !== TRANSACTIONS_TABLE_STATUS.rejectRequest
            )
          : [...value, data, TRANSACTIONS_TABLE_STATUS.acceptRequest, TRANSACTIONS_TABLE_STATUS.rejectRequest];
      } else {
        valueSet = value?.includes(data) ? value?.filter((val) => val !== data) : [...value, data];
      }
      return onChange(name, valueSet);
    }
    onChange(name, data);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    if (options && !isDisabled) {
      setIsMenuOpen(!isMenuOpen);
    }
  };

  const closeMenu = (e) => {
    const isClickOnMenu = e?.relatedTarget?.closest && e?.relatedTarget?.closest('.input-dropdown-menu');
    if (!isClickOnMenu && isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const handleBlur = (e) => {
    closeMenu(e);
    onBlur();
  };

  const menuContent = (
    <div
      className={`input-dropdown-menu ${menuDirection === MENU_DIRECTION.UP ? 'direction-up' : ''}`}
      role={'listbox'}
      id={menuId}
      tabIndex={-1}
      aria-label={label || name}
      style={usePortal ? menuStyle : undefined}
      onMouseDown={(e) => e?.preventDefault()}
    >
      {options &&
        options?.map((dataPoint, index) => (
          <CheckBox
            key={index}
            onChange={() => inputChange(dataPoint?.key)}
            isActive={(isMulti && value?.includes(dataPoint?.key)) || dataPoint?.key === value}
            showCheckBox={isMulti}
            value={dataPoint?.value}
            currency={dataPoint?.currency}
          />
        ))}
      {specificOptions &&
        specificOptions?.map((dataPoint, index) => (
          <CheckBox
            key={index}
            onChange={() => inputChange(dataPoint?.key)}
            isActive={(isMulti && value?.includes(dataPoint?.key)) || dataPoint?.key === value}
            showCheckBox={isMulti}
            value={null}
            content={dataPoint?.content}
          />
        ))}
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      onBlur={(e) => handleBlur(e)}
      role={'combobox'}
      aria-haspopup={'listbox'}
      aria-expanded={isMenuOpen}
      aria-controls={menuId}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e?.key === 'Enter' || e?.key === ' ') {
          e?.preventDefault();
          toggleMenu();
        }
      }}
      onClick={toggleMenu}
      className={inputClasses}
    >
      {topLabel && <p className={'top-label'}>{topLabel}</p>}
      {label && <label htmlFor={name}>{label + (isRequired ? '*' : '')}</label>}
      <input
        name={name}
        type={'text'}
        value={getValue() || ''}
        onChange={(e) => onChange(e)}
        placeholder={placeholder}
        disabled
        className={`${currency ? 'wallet-input' : ''} input`}
      />

      {currency && <span className={'currency-label'}>{currency}</span>}
      <div className={`input-icon eye ${error ? 'error' : ''}`}>{isMenuOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}</div>
      {isMenuOpen && (usePortal ? createPortal(menuContent, document?.body) : menuContent)}
      {(error || subText) && (
        <p
          className={`input-sub-text ${error ? 'input-error' : ''} ${isAbsoluteErrorPosition ? 'absolute-error' : ''}`}
        >
          {error || subText}
        </p>
      )}
    </div>
  );
};

InputDropDown.propTypes = {
  options: PropTypes.array,
  specificOptions: PropTypes.array,
  name: PropTypes.string,
  error: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object, PropTypes.any]),
  currency: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  subText: PropTypes.string,
  className: PropTypes.string,
  topLabel: PropTypes.string,
  menuDirection: PropTypes.string,
  isRequired: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSpecificValue: PropTypes.bool,
  isTransactionStatuses: PropTypes.bool,
  isAbsoluteErrorPosition: PropTypes.bool,
  usePortal: PropTypes.bool
};
